import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { TokenGuard, StandardURL } from "../App"; 

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  :root {
    --bg-base: #09090b;
    --surface: #18181b;
    --surface-hover: #27272a;
    --border: #3f3f46;
    --border-focus: #6366f1;
    --primary: #6366f1;
    --primary-hover: #4f46e5;
    --primary-dim: rgba(99, 102, 241, 0.15);
    --text-main: #f8fafc;
    --text-muted: #a1a1aa;
    --success: #10b981;
    --success-dim: rgba(16, 185, 129, 0.15);
    --warning: #f59e0b;
    --warning-dim: rgba(245, 158, 11, 0.15);
    --danger: #ef4444;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .dashboard-wrapper {
    background: var(--bg-base);
    color: var(--text-main);
    min-height: 100vh;
    font-family: 'Inter', sans-serif;
    position: relative;
    padding: 3rem 2rem;
  }

  .dashboard-container {
    position: relative;
    z-index: 1;
    max-width: 1000px;
    margin: 0 auto;
  }

  /* --- Header --- */
  .dash-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 3rem;
    padding-bottom: 1.5rem;
    border-bottom: 1px solid var(--border);
    flex-wrap: wrap;
    gap: 1.5rem;
  }

  .welcome-section h1 { font-size: 2rem; font-weight: 700; margin-bottom: 0.4rem; }
  .welcome-section p { color: var(--text-muted); font-size: 0.95rem; }

  /* --- Button --- */
  .btn-primary {
    background: var(--primary);
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 0.75rem 1.2rem;
    font-family: 'Inter', sans-serif;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
  .btn-primary:hover:not(:disabled) { background: var(--primary-hover); transform: translateY(-1px); }

  /* --- Vids Grid --- */
  .section-title { font-size: 1.25rem; font-weight: 600; margin-bottom: 1.5rem; }
  .vids-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1.5rem; }

  .vid-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 1.5rem;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
  }
  .vid-card:hover { border-color: var(--border-focus); transform: translateY(-3px); box-shadow: 0 10px 25px rgba(0,0,0,0.4); }

  .vid-header {
    display: flex; justify-content: space-between; align-items: center;
    margin-bottom: 1.2rem; padding-bottom: 1rem; border-bottom: 1px dashed var(--border);
  }
  .vid-title { font-size: 1.25rem; font-weight: 800; color: var(--text-main); }
  
  .status-badge { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; padding: 4px 10px; border-radius: 6px; }
  .status-completed { background: var(--success-dim); color: var(--success); }
  .status-pending { background: var(--warning-dim); color: var(--warning); }

  .vid-detail-row { display: flex; justify-content: space-between; align-items: center; font-size: 0.85rem; margin-bottom: 0.6rem; }
  .vid-detail-label { color: var(--text-muted); }
  .vid-detail-value { font-weight: 600; color: var(--text-main); text-transform: capitalize; }
  .vid-detail-value.mono { font-family: monospace; color: var(--text-muted); text-transform: none; }

  /* =========================================
     FIXED MODAL STYLES 
     ========================================= */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(5px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 1rem;
    animation: fadeIn 0.2s ease-out;
  }

  .modal-content {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 16px;
    width: 100%;
    max-width: 550px;
    max-height: 90vh; /* FIX: Prevents modal from extending off screen */
    overflow-y: auto; /* FIX: Allows scrolling inside the modal */
    padding: 2.5rem;
    position: relative;
    box-shadow: 0 25px 50px rgba(0,0,0,0.5);
  }

  /* Custom Scrollbar for Modal */
  .modal-content::-webkit-scrollbar { width: 6px; }
  .modal-content::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1.5rem;
  }

  .modal-title { font-size: 1.5rem; font-weight: 700; margin: 0; }
  .modal-subtitle { font-size: 0.9rem; color: var(--text-muted); margin-top: 0.5rem; line-height: 1.5; }

  .modal-close {
    background: transparent;
    border: none;
    color: var(--text-muted);
    font-size: 1.8rem;
    line-height: 1;
    cursor: pointer;
    transition: color 0.2s;
    padding: 0;
  }
  .modal-close:hover { color: var(--text-main); }

  .form-group {
    display: flex;
    flex-direction: column;
    margin-bottom: 1.25rem;
    width: 100%;
  }

  .form-label { font-size: 0.85rem; font-weight: 600; color: var(--text-muted); margin-bottom: 0.5rem; }

  .form-input, .form-select, .form-textarea {
    width: 100%;
    box-sizing: border-box; /* FIX: Keeps inputs from expanding past container */
    background: var(--bg-base);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text-main);
    font-family: 'Inter', sans-serif;
    font-size: 0.95rem;
    padding: 0.75rem 1rem;
    outline: none;
    transition: all 0.2s ease;
  }
  
  .form-input:focus, .form-select:focus, .form-textarea:focus {
    border-color: var(--border-focus);
    box-shadow: 0 0 0 3px var(--primary-dim);
  }

  .form-select {
    appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 1.2em;
    padding-right: 2.5rem;
  }

  .form-textarea { resize: vertical; min-height: 90px; }

  .char-count { font-size: 0.75rem; color: var(--text-muted); text-align: right; margin-top: 0.3rem; }

  .modal-actions {
    margin-top: 2rem;
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
  }

  .btn-secondary {
    background: transparent;
    color: var(--text-main);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 0.75rem 1.2rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }
  .btn-secondary:hover { background: var(--surface-hover); }

  .payment-alert {
    background: var(--warning-dim);
    border-left: 4px solid var(--warning);
    padding: 1.25rem;
    border-radius: 6px;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
    color: var(--text-main);
    line-height: 1.6;
  }

  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
`;

const LandingPage = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [vids, setVids] = useState([]);
  const [isLoadingVids, setIsLoadingVids] = useState(true);

  // Application Modal State
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingAuth, setIsUploadingAuth] = useState(false);
  const [authFileName, setAuthFileName] = useState("");
  const [formData, setFormData] = useState({
    senderid: "",
    category: "transactional",
    description: "",
    authorizationletter: "",
  });

  // Payment Modal State
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [selectedVidForPayment, setSelectedVidForPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    email: "",
    phone: "",
    channel: "mpesa"
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      fetchVids(parsedUser.unique);
    } else {
      setIsLoadingVids(false);
    }

    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchVids = async (userId, triggerPaymentForSenderId = null) => {
    setIsLoadingVids(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${StandardURL}/sms/apply/user/${userId}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      const fetchedVids = response.data.response || [];
      setVids(fetchedVids);

      if (triggerPaymentForSenderId) {
        const newlyCreated = fetchedVids.find(v => v.senderid === triggerPaymentForSenderId);
        if (newlyCreated && ['pending', 'inactive'].includes(newlyCreated.status?.toLowerCase())) {
          setSelectedVidForPayment(newlyCreated);
          setIsPaymentModalOpen(true);
        }
      }
    } catch (error) {
      console.error("Error fetching vids:", error);
    } finally {
      setIsLoadingVids(false);
    }
  };

  const handleApplyInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAuthFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataPayload = new FormData();
    formDataPayload.append("file", file);

    try {
      setIsUploadingAuth(true);
      const token = localStorage.getItem("token");
      const res = await axios.post(`${StandardURL}/sms/kyc/upload`, formDataPayload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });

      const fileReference = res.data?.coverUri || res.data?.response?.coverUri || "";

      setFormData((prev) => ({ ...prev, authorizationletter: fileReference }));
      setAuthFileName(file.name);
      toast.success("Authorization letter uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload authorization letter");
      console.error(error);
    } finally {
      setIsUploadingAuth(false);
    }
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    if (!user || !user.unique) return toast.error("User ID not found.");

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${StandardURL}/sms/apply/add/${user.unique}`, formData, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      toast.success("Sender ID application submitted successfully!");
      setIsSubmitting(false);
      setIsApplyModalOpen(false);
      
      const submittedSenderId = formData.senderid;
      setFormData({ senderid: "", category: "transactional", description: "", authorizationletter: "" });
      setAuthFileName(""); // Reset the uploaded file name display

      fetchVids(user.unique, submittedSenderId);
    } catch (error) {
      toast.error(error.response?.data?.response || "Failed to submit application.");
      setIsSubmitting(false);
    }
  };

  const handleVidClick = (vidObj) => {
    const status = vidObj.status?.toLowerCase();
    if (status === "completed" || status === "active") {
      localStorage.setItem('currentVid', vidObj.vid);
      navigate('/sms/videnrivonsnew');
    } else {
      setSelectedVidForPayment(vidObj);
      setIsPaymentModalOpen(true);
    }
  };

  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVidForPayment) return;

    setIsPaying(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${StandardURL}/sms/payment/apply-stk/${selectedVidForPayment.id}`, paymentForm, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      toast.success(`Payment request sent to ${paymentForm.phone}. Please check your device.`);
      setIsPaymentModalOpen(false);
      setPaymentForm({ email: "", phone: "", channel: "mpesa" });
    } catch (error) {
      toast.error(error.response?.data?.response || "Payment initialization failed.");
    } finally {
      setIsPaying(false);
    }
  };

  const formattedTime = new Intl.DateTimeFormat('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true
  }).format(currentTime);

  return (
    <TokenGuard>
      <style>{styles}</style>
      <div className="dashboard-wrapper">
        <div className="dashboard-container">
          
          <div className="dash-header">
            <div className="welcome-section">
              <h1>Welcome back, {user?.fullName || "User"}</h1>
              <p>{formattedTime}</p>
            </div>
            <button className="btn-primary" onClick={() => setIsApplyModalOpen(true)}>
              Apply for Sender ID
            </button>
          </div>

          <h2 className="section-title">Your Sender IDs</h2>
          
          {isLoadingVids ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
              <Oval height={40} width={40} color="#6366f1" secondaryColor="rgba(99,102,241,0.2)" visible={true} />
            </div>
          ) : (
            <div className="vids-grid">
              {vids.length > 0 ? (
                vids.map((vid, index) => {
                  const isReady = ["completed", "active"].includes(vid.status?.toLowerCase());
                  
                  return (
                    <div key={index} className="vid-card" onClick={() => handleVidClick(vid)}>
                      <div className="vid-header">
                        <span className="vid-title">{vid.senderid || "N/A"}</span>
                        <span className={`status-badge ${isReady ? 'status-completed' : 'status-pending'}`}>
                          {isReady ? 'Ready' : 'Setup Required'}
                        </span>
                      </div>
                      
                      <div className="vid-detail-row">
                        <span className="vid-detail-label">System VID</span>
                        <span className="vid-detail-value mono">{vid.vid}</span>
                      </div>
                      <div className="vid-detail-row">
                        <span className="vid-detail-label">Category</span>
                        <span className="vid-detail-value">{vid.category}</span>
                      </div>
                      <div className="vid-detail-row" style={{ marginTop: '1.2rem', justifyContent: 'flex-start' }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: isReady ? 'var(--success)' : 'var(--warning)' }}>
                          {isReady ? "Open Dashboard →" : "Complete Setup Payment →"}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '4rem 2rem', background: 'rgba(255, 255, 255, 0.02)', border: '1px dashed var(--border)', borderRadius: '12px', color: 'var(--text-muted)' }}>
                  <p>You haven't applied for any Sender IDs yet.</p>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* --- Apply Modal --- */}
      {isApplyModalOpen && (
        <div className="modal-overlay" onClick={(e) => { if (e.target.className === 'modal-overlay') setIsApplyModalOpen(false) }}>
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h3 className="modal-title">New Sender ID</h3>
                <p className="modal-subtitle">Register a new alphanumeric brand identity.</p>
              </div>
              <button className="modal-close" onClick={() => setIsApplyModalOpen(false)}>&times;</button>
            </div>

            <form onSubmit={handleApplySubmit}>
              <div className="form-group">
                <label className="form-label">Sender ID</label>
                <input type="text" name="senderid" className="form-input" placeholder="e.g. MYCOMPANY" maxLength={10} required value={formData.senderid} onChange={handleApplyInputChange} />
                <div className="char-count">{formData.senderid.length}/10</div>
              </div>

              <div className="form-group">
                <label className="form-label">Category</label>
                <select name="category" className="form-select" required value={formData.category} onChange={handleApplyInputChange}>
                  <option value="transactional">Transactional</option>
                  <option value="promotional">Promotional</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Description of Use</label>
                <textarea name="description" className="form-textarea" placeholder="Briefly describe intent..." required value={formData.description} onChange={handleApplyInputChange} />
              </div>

              <div className="form-group">
                <label className="form-label">Authorization Letter (Upload PDF only)</label>
                <input 
                  type="file" 
                  name="authorizationletter" 
                  className="form-input" 
                  accept="application/pdf"
                  required={!formData.authorizationletter}
                  onChange={handleAuthFileUpload} 
                />
                
                {isUploadingAuth && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Oval height={14} width={14} color="var(--primary)" secondaryColor="var(--primary-dim)" /> Uploading...
                  </div>
                )}
                
                {authFileName && !isUploadingAuth && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--success)', marginTop: '0.5rem', fontWeight: 500 }}>
                    ✓ {authFileName} uploaded successfully
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setIsApplyModalOpen(false)}>Cancel</button>
                <button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={isSubmitting || isUploadingAuth || !formData.authorizationletter}
                >
                  {isSubmitting ? <Oval height={18} width={18} color="#fff" /> : "Submit Application"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- Payment Modal --- */}
      {isPaymentModalOpen && selectedVidForPayment && (
        <div className="modal-overlay" onClick={(e) => { if (e.target.className === 'modal-overlay') setIsPaymentModalOpen(false) }}>
          <div className="modal-content" style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <div>
                <h3 className="modal-title">Setup Fee Required</h3>
                <p className="modal-subtitle">Activate <strong>{selectedVidForPayment.senderid}</strong> to continue.</p>
              </div>
              <button className="modal-close" onClick={() => setIsPaymentModalOpen(false)}>&times;</button>
            </div>

            <div className="payment-alert">
              <strong>Cost Breakdown:</strong><br /><br />
              • Transactional Sender IDs: KES 8,000<br />
              • Promotional Sender IDs: KES 9,000<br /><br />
              <span style={{ color: 'var(--success)', fontWeight: 800, fontSize: '1.1rem' }}>
                Amount Due: KES {selectedVidForPayment.category?.toLowerCase() === 'promotional' ? '9,000' : '8,000'}
              </span>
            </div>

            <form onSubmit={handlePaymentSubmit}>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input type="email" name="email" className="form-input" placeholder="admin@yourcompany.com" required value={paymentForm.email} onChange={handlePaymentInputChange} />
              </div>

              <div className="form-group">
                <label className="form-label">Mobile Number</label>
                <input type="tel" name="phone" className="form-input" placeholder="2547XXXXXXXX" required value={paymentForm.phone} onChange={handlePaymentInputChange} />
              </div>

              <div className="form-group">
                <label className="form-label">Network Provider</label>
                <select name="channel" className="form-select" required value={paymentForm.channel} onChange={handlePaymentInputChange}>
                  <option value="mpesa">M-Pesa</option>
                  <option value="airtelmoney">Airtel Money</option>
                </select>
              </div>

              <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
                <button type="submit" className="btn-primary" style={{ width: '100%' }} disabled={isPaying}>
                  {isPaying ? <Oval height={18} width={18} color="#fff" /> : "Trigger STK Push"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </TokenGuard>
  );
};

export default LandingPage;